import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Auth } from '@core/auth/services/auth';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { OrganizationStore } from '@features/organizations/store/organization.store';

/**
 * Guard to ensure user has an organization context selected
 * Priority order:
 * 1. URL path param (:slug) - from route like /organizations/:slug/settings
 * 2. URL query param (?org=slug) - from query string
 * 3. Currently selected org in store
 * 4. LocalStorage persisted org
 * 5. First available org (auto-select)
 * 6. Offer joining/organization selection if user has no orgs
 */
export const organizationGuard: CanActivateFn = async (route, state) => {
  const auth = inject(Auth);
  const router = inject(Router);
  const currentOrgStore = inject(CurrentOrganizationStore);
  const organizationStore = inject(OrganizationStore);

  // Check if user is authenticated
  const user = auth.getUser() || await auth.refreshUserData();
  if (!user) {
    router.navigate(['/login'], {
      queryParams: { returnUrl: state.url },
    });
    return false;
  }

  // 1. Priority: org slug in path, then query
  let orgSlugFromPath = route.paramMap.get('slug');
  if (!orgSlugFromPath && route.parent) {
    orgSlugFromPath = route.parent.paramMap.get('slug');
  }
  const orgSlugFromQuery = route.queryParams['org'];
  const orgSlugFromUrl = orgSlugFromPath || orgSlugFromQuery;

  if (orgSlugFromUrl) {
    try {
      const currentOrg = currentOrgStore.organization();
      if (!currentOrg || currentOrg.slug !== orgSlugFromUrl) {
        await currentOrgStore.switchToOrganizationBySlug(
          orgSlugFromUrl,
          !!orgSlugFromQuery,
        );
      }
      return true;
    } catch {
      router.navigate(['/organizations']);
      return false;
    }
  }

  // 2. Use already selected organization
  const currentOrg = currentOrgStore.organization();
  if (currentOrg) {
    const isOrgSpecificRoute = !!route.paramMap.get('slug');
    if (!isOrgSpecificRoute && !orgSlugFromQuery) {
      const currentPath = state.url.split('?')[0];

      if (currentPath === '/' || currentPath === '') {
        router.navigate(['/organizations', currentOrg.slug, 'dashboard']);
        return false;
      } else if (currentPath.startsWith('/dashboard')) {
        router.navigate(['/organizations', currentOrg.slug, 'dashboard']);
        return false;
      } else if (currentPath && !currentPath.startsWith('/organizations/')) {
        const pathSegment = currentPath.replace('/', '');
        router.navigate(['/organizations', currentOrg.slug, pathSegment]);
        return false;
      }
    }
    return true;
  }

  // 3. localStorage fallback (only if no explicit URL org)
  const persistedOrgId = currentOrgStore.getPersistedOrganizationId();
  if (persistedOrgId && !orgSlugFromPath && !orgSlugFromQuery) {
    try {
      await currentOrgStore.switchToOrganization(persistedOrgId, false);
      const persistedOrg = currentOrgStore.organization();
      if (persistedOrg) {
        router.navigate(['/organizations', persistedOrg.slug, 'dashboard']);
        return false;
      }
    } catch {
      // Continue to auto-select
    }
  }

  // 4. Auto-select first organization
  try {
    // Never reuse a filtered list or a previous account's cached organizations.
    await organizationStore.loadOrganizations({ page: 1, limit: 10 });
    if (organizationStore.hasError()) {
      router.navigate(['/organizations']);
      return false;
    }

    const organizations = organizationStore.organizations();

    if (!organizations || organizations.length === 0) {
      router.navigate(['/organizations']);
      return false;
    }

    const firstOrg = organizations[0];
    currentOrgStore.setCurrentOrganization(firstOrg, false);

    router.navigate(['/organizations', firstOrg.slug, 'dashboard']);
    return false;
  } catch {
    router.navigate(['/organizations']);
    return false;
  }
};
